'use client';

import type {
  FastShopFlowSnapshot,
  FastShopFlowState,
} from './fast-shop.utils';
import type {
  FastShopLowBalanceTrigger,
} from '@/queries/fast-shop/fast-shop.schema';
import { useEffect, useRef } from 'react';
import openModal from '@/modals/utils/openModal';
import {
  FAST_SHOP_LOW_BALANCE_TRIGGER_EVENT,
} from '@/queries/fast-shop/fast-shop.sockets';
import { Currency } from '@/queries/types/Currency.types';
import {
  canAcceptFastShopTrigger,
  FAST_SHOP_INITIAL_FLOW,
  FAST_SHOP_STATE_CHANGE_EVENT,
  shouldPendFastShopTrigger,
} from './fast-shop.utils';

function isDocumentVisible() {
  return typeof document === 'undefined' || document.visibilityState === 'visible';
}

function isBrowserFullscreen(appFullscreen: boolean) {
  return appFullscreen || Boolean(document.fullscreenElement);
}

function dispatchStateChange(change: CustomEventMap[typeof FAST_SHOP_STATE_CHANGE_EVENT]['detail']) {
  window.dispatchEvent(new CustomEvent(FAST_SHOP_STATE_CHANGE_EVENT, { detail: change }));
}

function isRoundEscalation(
  current: FastShopFlowSnapshot,
  trigger: FastShopLowBalanceTrigger,
) {
  const currentTrigger = current.pendingTrigger ?? current.activeTrigger;

  return trigger.source === 'round' && currentTrigger?.source === 'session';
}

export function FastShopFlowController() {
  // Keep the flow snapshot in a ref so websocket/fullscreen listeners always read the latest state.
  const flowRef = useRef(FAST_SHOP_INITIAL_FLOW);
  const appFullscreenRef = useRef(false);
  const openingTimerRef = useRef<number | null>(null);

  useEffect(() => {
    const transitionTo = (
      nextState: FastShopFlowState,
      trigger: FastShopLowBalanceTrigger | null,
      reason: string,
      flowPatch: Partial<FastShopFlowSnapshot> = {},
    ) => {
      const previousState = flowRef.current.state;

      flowRef.current = {
        ...flowRef.current,
        ...flowPatch,
        state: nextState,
      };

      // Broadcast state changes for temporary debugging / future Fast Offers rendering hooks.
      dispatchStateChange({
        from: previousState,
        to: nextState,
        trigger,
        reason,
      });
    };

    const moveToLoading = (trigger: FastShopLowBalanceTrigger) => {
      // The popup shell opens before Fast Offers are requested, matching the documented sequence flow.
      transitionTo('opening', trigger, 'Fast Shop shell can open now', {
        activeTrigger: trigger,
        pendingTrigger: null,
      });

      if (trigger.source === 'round') {
        openModal('deposit', { variant: 'fast-shop', lowBalanceGtmSource: 'low-balance' });
      }

      if (trigger.source === 'session') {
        openModal('game-session-low-balance');
      }

      if (openingTimerRef.current !== null) {
        window.clearTimeout(openingTimerRef.current);
      }

      openingTimerRef.current = window.setTimeout(() => {
        openingTimerRef.current = null;

        if (flowRef.current.activeTrigger !== trigger) {
          return;
        }

        // Move to loading on the next tick so the shell has a chance to mount first.
        transitionTo('loading', trigger, 'Fast Offers request can start');
      }, 0);
    };

    const openOrPend = (trigger: FastShopLowBalanceTrigger) => {
      // Fullscreen or hidden tabs can block modal UI, so keep the trigger pending until UI is available.
      const shouldPend = shouldPendFastShopTrigger(
        isBrowserFullscreen(appFullscreenRef.current),
        isDocumentVisible(),
      );

      if (shouldPend) {
        transitionTo('pending', trigger, 'Browser state blocks opening now', {
          activeTrigger: null,
          pendingTrigger: trigger,
        });
        return;
      }

      moveToLoading(trigger);
    };

    const handleTrigger = (event: Event) => {
      // Both game.round.low-balance and game.session.low-balance are normalized into this browser event.
      const trigger = (event as CustomEventMap[typeof FAST_SHOP_LOW_BALANCE_TRIGGER_EVENT]).detail;
      const current = flowRef.current;

      // GC sessions are replenished by the automatic top-up flow and must not
      // show either the session popup or the round-triggered Fast Shop.
      if (trigger.currency === Currency.GC) {
        return;
      }

      // A round trigger is more urgent than a session-threshold trigger: it means
      // the same bet can no longer be repeated. Let it replace an active or pending
      // session popup, while all same-priority repeats remain deduplicated.
      if (!canAcceptFastShopTrigger(current.state) && !isRoundEscalation(current, trigger)) {
        return;
      }

      openOrPend(trigger);
    };

    const flushPendingIfAvailable = () => {
      const current = flowRef.current;

      // Pending triggers should open only after the user leaves fullscreen or returns to a visible tab.
      if (current.state !== 'pending' || !current.pendingTrigger) {
        return;
      }

      if (shouldPendFastShopTrigger(isBrowserFullscreen(appFullscreenRef.current), isDocumentVisible())) {
        return;
      }

      const trigger = current.pendingTrigger;
      moveToLoading(trigger);
    };

    const handleAppFullscreen = (event: Event) => {
      appFullscreenRef.current = (event as CustomEventMap['app:fullscreen']).detail === true;
      flushPendingIfAvailable();
    };

    const handleBrowserAvailabilityChange = () => {
      flushPendingIfAvailable();
    };

    window.addEventListener(FAST_SHOP_LOW_BALANCE_TRIGGER_EVENT, handleTrigger);
    window.addEventListener('app:fullscreen', handleAppFullscreen);
    document.addEventListener('fullscreenchange', handleBrowserAvailabilityChange);
    document.addEventListener('visibilitychange', handleBrowserAvailabilityChange);

    return () => {
      window.removeEventListener(FAST_SHOP_LOW_BALANCE_TRIGGER_EVENT, handleTrigger);
      window.removeEventListener('app:fullscreen', handleAppFullscreen);
      document.removeEventListener('fullscreenchange', handleBrowserAvailabilityChange);
      document.removeEventListener('visibilitychange', handleBrowserAvailabilityChange);

      if (openingTimerRef.current !== null) {
        window.clearTimeout(openingTimerRef.current);
        openingTimerRef.current = null;
      }
    };
  }, []);

  return null;
}
